import { Button, Col, Form, Input, Row, Spin } from 'antd';
import '../../styles/_orgs.scss';
import {
    PlaceOfServiceRequest,
    PlaceOfServiceResponse,
} from '../../models/OrganizationModels';
import { useCreatePlaceOfServiceMutation } from '../../features/organization/organizationService';
import useOrderToast from '../../hooks/useOrderToast';

export type OrganizationFieldType = {
    serviceName: string;
    serviceId: string;
    serviceCode: string;
};
type Props = {
    teamId: string;
    placeOfService?: PlaceOfServiceResponse | null;
    closeModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const ExtraFieldLabel = ({ title }: { title: string }) => {
    return <span className="ms-3 text-semi-black-xs">{title}</span>;
};
const ValidationMessage = ({ message }: { message: string }) => {
    return <span className="ms-3">{message}</span>;
};

const OrganizationPlaceOfService: React.FC<Props> = ({
    teamId,
    placeOfService,
    closeModal,
}) => {
    const [form] = Form.useForm();
    const [createPlaceOfService, { isLoading }] =
        useCreatePlaceOfServiceMutation();
    const { toast } = useOrderToast();
    const isUpdate = !!placeOfService;

    const handleSubmit = async () => {
        const values: OrganizationFieldType = await form.validateFields();
        try {
            const requestData: PlaceOfServiceRequest = {
                id: placeOfService?.id,
                teamId: teamId,
                serviceName: values.serviceName,
                serviceCode: values.serviceCode,
                serviceId: values.serviceId,
            };
            const response: any = await createPlaceOfService(requestData);
            if (response.data) {
                form.resetFields();
                closeModal(false);
                toast(
                    isUpdate
                        ? 'The place of service is successfully updated.'
                        : 'The place of service is successfully added to the team.',
                    'success',
                    2,
                );
            }
            else{
                toast(
                    `The place of service ${isUpdate ? 'update' : 'creation'} failed. Please try again.`,
                    'error',
                    2,
                );
            }
        } catch (error) {
            toast(
                `The place of service ${isUpdate ? 'update' : 'creation'} failed. Please try again.`,
                'error',
                2,
            );
        }
    };
    return (
        <Spin spinning={isLoading}>
        <Form
            form={form}
            name="basic"
            layout="horizontal"
            autoComplete="off"
            className="orgs-new-team-form"
        >
            <Row>
                <Col span={24}>
                    <Form.Item<OrganizationFieldType>
                        label={'Place of Service Name'}
                        name="serviceName"
                        initialValue={placeOfService?.name}
                        labelCol={{ span: 5 }}
                        wrapperCol={{ span: 11 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Place of Service Name" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Place of Service Name should align with RxLogic" />
                        }
                    >
                        <Input
                            placeholder="Enter Place of Service Name"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<OrganizationFieldType>
                        label={'Place of Service ID'}
                        name="serviceId"
                        initialValue={placeOfService?.rxLogicId}
                        labelCol={{ span: 5 }}
                        wrapperCol={{ span: 11 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Place of Service ID" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Place of Service ID should align with RxLogic" />
                        }
                    >
                        <Input
                            placeholder="Enter Place of Service ID"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<OrganizationFieldType>
                        label={'Place of Service Code'}
                        name="serviceCode"
                        initialValue={placeOfService?.code}
                        labelCol={{ span: 5 }}
                        wrapperCol={{ span: 11 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Place of Service Code" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Place of Service Code should align with RxLogic" />
                        }
                    >
                        <Input
                            placeholder="Enter Place of Service Code"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row className="mt-5">
                <Col span={4} offset={5}>
                    <Button
                        loading={isLoading}
                        disabled={isLoading}
                        type="primary"
                        className="w-100 ms-3 bg-light-blue border-0"
                        shape="round"
                        onClick={handleSubmit}
                        size="large"
                    >
                        Save
                    </Button>
                </Col>
            </Row>
        </Form>
        </Spin>
    );
};

export default OrganizationPlaceOfService;
